<template>
  <div :id="rootElementId"></div>
</template>

<script>
import '../../utils/swoopy-drag';

import ScatterService from '../../services/scatter.service';
import { formatNumber, round } from '../../utils/functions';
import { loadFonts,copyAndAppendElement,drawImageBase64 } from '../../utils/exportToPNG';
import * as d3 from "d3v4";

  export default {
    name: 'ScatterPlotChart',
    props: {
      size:{
        type:Number,
        required: true
      },
      margin:{
        type:Object,
        default() {
          return {
            top:30,
            right:50,
            bottom: 100,
            left: 75
          }
        }
      },
      chartType: {
        type: String,
        default: 'Teams'
      },
      seasons: {
        type: Array,
        default(){
          return []
        }
      },
      scatterMetrics: {
        type: Array,
        required: true
      },
      axisX: {
        type: Object,
        required: true
      },
      axisY: {
        type: Object,
        required: true
      },
      bubbleSize:{
        type: String,
        default: "byMarketValue"
      },
      is90:{
        type: Boolean,
        default: false
      },
      light:{
        type: Boolean,
        default: true
      },
      fieldPositions:{
        type: Array,
        default: () => {
          //All fieldPositionIds
          return [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16]
        }
      },
      minMinutes:{
        type: Number,
        default: 450
      },
      maxAge:{
        type: Number,
        default: 40
      },
      limit:{
        type: Number,
        default: 100
      },
      id:{
        type: String,
        required: true
      }
    },
    computed: {
      WIDTH_SVG(){
        return this.size;
      },
      HEIGHT_SVG(){
        return this.size * this.heightProportion;
      },
      WIDTH(){
        if(this.margin){
          return this.size - this.margin.left - this.margin.right;
        }
        return this.size;
      },
      HEIGHT(){
        if(this.margin){
          return ( this.size * this.heightProportion ) - this.margin.top - this.margin.bottom;
        }
        return this.size * this.heightProportion;
      },

      xScale(){
        return d3.scaleLinear().range([0, this.WIDTH]);
      },
      yScale(){
        return d3.scaleLinear().range([this.HEIGHT,0]);
      },
      radius(){
        return d3.scaleSqrt().range([5, 20]);
      },
      xRange() {
        if(this.margin){
          return d3.scaleLinear().rangeRound([-this.margin.left + 20, this.WIDTH]);
        }
        return d3.scaleLinear().rangeRound([20, this.WIDTH]);
      },
      yRange() {
        return d3.scaleLinear().rangeRound([0, this.HEIGHT + 20]);
      },
      yRangeMedian() {
        return d3.scaleLinear().rangeRound([0, this.HEIGHT + 40]);
      },
      rootElementId(){
        return `scatter-plot-${this.id}`
      }
    },
    data(){
      return {
        title: '',
        heightProportion: 0.635
      }
    },
    async mounted(){
      this.title = this.axisX.text + " vs " + this.axisY.text;
      this.renderScatterPlot();
    },
    methods:{
      async getScatterData(){
        if(this.fieldPositions.length){
          let scatterResponse;

          if(this.chartType === 'Teams'){
            scatterResponse = await ScatterService.getScatterTeams(this.seasons, this.axisX.cN, this.axisX.stId, this.axisY.cN, this.axisY.stId, this.is90, this.limit);
          }else{
            scatterResponse = await ScatterService.getScatterPlayers(this.seasons, this.fieldPositions, this.minMinutes,
              this.maxAge, this.axisX.id, this.axisY.id, this.is90, this.bubbleSize, this.limit);
          }

          this.$emit("dataLoaded",scatterResponse.data);
          return scatterResponse.data;
        }
        return null;
      },
      async renderScatterPlot() {
        d3.select(`#${this.rootElementId}`).selectAll("*").remove();
        //xGSavedPerShot, xGPerShot, xGPerShotAgainst
        const stats2Decimals = [27, 44, 110];
        let data = await this.getScatterData();
        //Print nothing if there si no data
        if(!data)
          return;

        let svg = d3.select(`#${this.rootElementId}`)
          .append('svg')
          .attr('width', this.WIDTH_SVG)
          .attr('height', this.HEIGHT_SVG)
          .append('g')
          .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')');

        let tooltip = d3.select("body").append("div").attr("class", "toolTip");

        let COLORS_ARRAY = this.getScatterColors(data.colors, this.axisX.r, this.axisY.r);
        let fillColor = d3.scaleOrdinal(COLORS_ARRAY);

        this.xScale.domain(d3.extent(data.bubbles, function(d){
          return d.x;
        }));

        this.yScale.domain(d3.extent(data.bubbles, function(d){
          return d.y;
        }));

        this.radius.domain(d3.extent(data.bubbles, function(d){
          return d.size;
        }));

        var medianYAxis = this.HEIGHT * (data.medianY - data.minY) / (data.maxY - data.minY)
        var halfYAxis = this.HEIGHT / 2

        svg.append('g')
          .attr('transform', 'translate(0, ' + this.HEIGHT + ')')
          .attr('class', 'x axis2')
          .call(d3.axisTop(this.xRange).tickSize(0).ticks(0));

        svg.append('g')
          .attr('transform', 'translate(0, 0)')
          .attr('class', 'x axis2')
          .call(d3.axisTop(this.xRange).tickSize(0).ticks(0));

        var medianXaxis = this.WIDTH * (data.medianX - data.minX) / (data.maxX - data.minX)
        var halfXAxis = this.WIDTH / 2

        svg.append('g')
          .attr('transform', 'translate(0,0)')
          .attr('class', 'y axis2')
          .call(d3.axisRight(this.yRange).tickSize(0).ticks(0));

        svg.append('g')
        .attr('transform', 'translate(' + (this.WIDTH) + ',0)')
          .attr('class', 'y axis2')
          .call(d3.axisRight(this.yRange).tickSize(0).ticks(0));

        let logoX = this.$vuetify.breakpoint.xlOnly ? (this.WIDTH - ((this.WIDTH * 0.35) / 3.75)) : (this.WIDTH - ((this.WIDTH * 0.35) / 3.5))
        let logoY = this.$vuetify.breakpoint.xlOnly ? this.HEIGHT + (this.margin.bottom / 1.5) : this.HEIGHT + (this.margin.bottom / 1.75)
        let logo  = require(`@/assets/img/driblab_color.png`);

        drawImageBase64(svg, logoX, logoY, this.WIDTH * 0.35,this.WIDTH * 0.05, logo,'','',false)

        // AXIS Label
        // For x-axis, it's at (10, 10), and for y-axis at (width, height-10).
        //Y AXIS. Just show MedianY if is not 0
        if(data.medianY && data.medianY > 0){
          svg.append('g')
            .attr('transform', 'translate(0,' + (this.HEIGHT - medianYAxis) + ')')
            .attr('class', (this.light) ? 'x axis' : 'x axisDark')
            .call(d3.axisTop(this.xRange).tickSize(0).ticks(0));
          svg.append('text')
            .attr('x', - this.margin.left + 20)
            .attr('y', (this.HEIGHT - medianYAxis) - 5)
            .attr('text-anchor', 'start')
            .attr('class', (this.light) ? 'label' : 'label-dark')
            .style("font-family", "NeueMontreal")
            .text(`${this.$t("general.median")}: ` + (data.medianY && !isNaN(data.medianY) ? formatNumber(data.medianY.toFixed(1)) : 0) );
        }

        // X AXIS
        svg.append('g')
          .attr('transform', 'translate(' + medianXaxis  + ',0)')
          .attr('class', (this.light) ? 'y axis' : 'y axisDark')
          .call(d3.axisRight(this.yRangeMedian).tickSize(0).ticks(0));
        svg.append('text')
          .attr('x', medianXaxis)
          .attr('y', this.HEIGHT + 58)
          .attr('text-anchor', 'middle')
          .attr('class', (this.light) ? 'label' : 'label-dark')
          .style("font-family", "NeueMontreal")
          .text(`${this.$t("general.median")}: ` + (data.medianX && !isNaN(data.medianX) ? formatNumber(data.medianX.toFixed(1)) : 0));

  /*---------------------
  /     Print Lines
  ---------------------*/

        //Show Regression
        // svg.insert("svg:line",':first-child')
        //   .attr('x1', this.xScale(data.linearX1))
        //   .attr('x2', this.xScale(data.linearX2))
        //   .attr('y1', this.yScale(data.linearY1))
        //   .attr('y2', this.yScale(data.linearY2))
        //   .attr("fill", "none")
        //   .attr('stroke-width', 2.5)
        //   .attr('stroke', "#fc9d05");

        // HALF X
        svg.append('g')
        .attr('transform', 'translate(' + (halfXAxis) + ',0)')
          .attr('class', 'y axis2')
          .call(d3.axisRight(this.yRange).tickSize(0).ticks(0));
        svg.append('text')
          .attr('x', this.WIDTH / 2)
          .attr('y', this.HEIGHT + 35)
          .attr('text-anchor', 'middle')
          .attr('class', (this.light) ? 'label-light' : 'label-dark')
          .style("font-family", "NeueMontreal")
          .text(formatNumber(((data.maxX + data.minX) / 2).toFixed(1)));

        // HALF Y
        svg.append('g')
          .attr('transform', 'translate(0, ' + (halfYAxis) + ')')
          .attr('class', 'x axis2')
          .call(d3.axisTop(this.xRange).tickSize(0).ticks(0));
        svg.append('text')
          .attr('x', - this.margin.left + 20)
          .attr('y', (this.HEIGHT / 2) - 5)
          .attr('text-anchor', 'start')
          .attr('class', (this.light) ? 'label-light' : 'label-dark')
          .style("font-family", "NeueMontreal")
          .text(formatNumber(((data.maxY + data.minY).toFixed(1) / 2).toFixed(1)));



        var cornerTextArea = svg.selectAll('.cornerTextArea')
          .attr('class', 'cornerTextArea')
          .data([
            `${this.$t("general.many")} ${this.axisY.text}`,
            `${this.$t("general.many")} ${this.axisY.text}`,
            `${this.$t("general.few")} ${this.axisY.text}`,
            `${this.$t("general.few")} ${this.axisY.text}`,
            `${this.$t("general.few")} ${this.axisX.text}`,
            `${this.$t("general.many")} ${this.axisX.text}`,
            `${this.$t("general.few")} ${this.axisX.text}`,
            `${this.$t("general.many")} ${this.axisX.text}`,
          ])
          .enter().append('g');

        var cornerText = cornerTextArea.append('text')
          .attr('x', (d, i) => {
            if (i % 2 === 0)
              return 10;
            else
              return this.WIDTH - 5;
          })
          .attr('y', (d, i) => {
            if (i < 2)
              return 20;
            else if (i < 4)
              return this.HEIGHT - 50;
            else if (i < 6)
              return 45;
            else
              return this.HEIGHT - 25;
          })
          .attr('text-anchor', function(d, i) {
            if (i % 2 === 0)
              return 'start';
            else
              return 'end';
          })
          .attr('class', (d, i) => {
            var labelClass = (this.light) ? 'cornerLegendText' : 'cornerLegendTextDark';
            let greenQuarter = 0;
            let blueQuarter = 1;
            let yellowQuarter = 3;
            //Just X reversed
            if(this.axisX.r && !this.axisY.r){
              greenQuarter = 1;
              blueQuarter = 0;
              yellowQuarter = 2;
            }
            //Just Y reversed
            if(!this.axisX.r && this.axisY.r){
              greenQuarter = 2;
              blueQuarter = 3;
              yellowQuarter = 1;
            }
            //Both are reversed
            if(this.axisX.r && this.axisY.r){
              greenQuarter = 3;
              blueQuarter = 2;
              yellowQuarter = 0;
            }

            if (i % 4 === greenQuarter)
              return labelClass + " legendTextGreenish";
            else if (i % 4 === blueQuarter)
              return labelClass + " legendTextBlueish";
            else if (i % 4 === yellowQuarter)
              return labelClass + " legendTextYellowish";
            else
              return labelClass + " legendTextRedish";

            return labelClass;
          })
          .style("font-size", "14px")
          .style("font-family", "NeueMontreal")
          .html(function(d) {return d});

          cornerTextArea
          .on("mouseover", (d) => {
            var selection = d3.select(cornerTextArea);

            cornerTextArea.selectAll((this.light) ? '.cornerLegendText' : '.cornerLegendTextDark')
            .style("cursor","pointer")
            .call(
              d3.drag()
              .on("drag", function () {
                var selection = d3.select(this);

                selection
                  .attr("transform", "translate(" + (d3.event.x - selection.attr("x")) + "," + (d3.event.y - selection.attr("y")) + ")")
                  .style({cursor: "crosshair"});
              })
            );
          })
          .on("dblclick", function(d)  {
            const selection = d3.select(this);
            selection.remove();
          })


        var bubbleArea = svg.selectAll('.bubbleArea')
          .attr('class', 'bubbleArea')
          .data(data.bubbles)
          .enter().append('g')

        var bubble = bubbleArea.append('circle')
          .style("opacity", 0.6)
          .attr('class', 'bubble')
          .attr('id', d => d.id)
          .attr('cx', (d) => { return this.xScale(d.x);})
          .attr('cy', (d) => { return this.yScale(d.y); })
          .attr('r', (d) => {
            return this.radius(d.size * 0.85);
          })
          .attr('stroke-width', 2.5)
          .attr('stroke', function(d){ return fillColor(d.tag); });

        bubble
        .on('mouseenter',(d) => {
          bubble
            .attr('stroke-width', 3)

          var bubbleId = document.getElementById(d.id);
          var xAxisNumberOfDecimals = 1;
          var yAxisNumberOfDecimals = 1;
          if(stats2Decimals.includes(this.axisX.id))
            xAxisNumberOfDecimals = 2;
          if(stats2Decimals.includes(this.axisY.id))
            yAxisNumberOfDecimals = 2;

          let tooltipPropertSide = this.xScale(d.x) > (this.WIDTH_SVG / 2) ? 'right' : 'left';
          let tooltipXPos = tooltipPropertSide === 'right' ? window.innerWidth - bubbleId.getBoundingClientRect().left : bubbleId.getBoundingClientRect().left

          tooltip
            .style(tooltipPropertSide, tooltipXPos + "px")
            .style("top", (bubbleId.getBoundingClientRect().top + window.scrollY - 110) + "px")
            .style("display", "inline-block")
            .html("<h5 style='font-family:NeueMontreal;font-weight:500'><b>" + d.bubbleName + "</b> (" + d.bubbleInfo + ")</h5>" +
                  "<span>" + d.bubbleGroup + "</span></br>" +
                  "<span>" + this.axisX.text + "&nbsp;<span class='toolTipNumber' style='font-weight:600'>" + 
                    formatNumber(round(d.x, xAxisNumberOfDecimals)) + " " + this.axisX.u + "</span></span></br>" + //.toFixed(xAxisNumberOfDecimals)
                  "<span>" + this.axisY.text + "&nbsp;<span class='toolTipNumber' style='font-weight:600'>" + 
                    formatNumber(round(d.y, yAxisNumberOfDecimals)) + " " + this.axisY.u + "</span></span>"); //.toFixed(yAxisNumberOfDecimals)
        })
        .on('mouseout',function() {
          tooltip.style("display", "none").style("left",'').style("right",'');
        });

        var self = this;
        bubbleArea
        .on("click", function(d) {
          var selection = d3.select(this);

          selection.selectAll('.bubble')
            .filter(function(dBubble){ return dBubble.bubbleName == d.bubbleName; })
            .attr('stroke-width', 2.5)
            .attr('stroke', 'black');

          selection
            .selectAll((self.light) ? 'text' : 'text')
            .data([d])
            .enter()
            .append((self.light) ? 'text' : 'text')
            .style("font-size", "12px")
            .style("font-family", "NeueMontreal")
            .attr("class", () => {
              return (self.light) ? 'label shadow ' + self.getRightClassByColor(fillColor(selection.data()[0].tag)) : 'label-dark shadow-dark ' + self.getRightClassByColor(fillColor(selection.data()[0].tag)); })
            .attr("cx", 100)
            .attr("x", self.xScale(selection.data()[0].x) + "px")
            .attr("y", self.yScale(selection.data()[0].y) + "px")
            .html((d) => {
              return "<tspan>" + selection.data()[0].bubbleName + "</tspan>"
                  + "<tspan dy='1.2em' x=" + self.xScale(selection.data()[0].x) + ">" + ( self.chartType === "Teams" ? "#" + selection.data()[0].bubbleInfo + " at " : "") + selection.data()[0].bubbleGroup + "</tspan>";
              })

          bubbleArea.selectAll(".label,.label-dark")
          .style("cursor","pointer")
          .call(
            d3.drag()
            .on("drag", function() {
              const selection = d3.select(this);

              const dataPoint = selection.data()[0];

              selection
                .attr("transform", "translate(" + (d3.event.x + 20 - dataPoint.x) + "," + (d3.event.y - dataPoint.y) + ")")
                .style({cursor: "crosshair"});

              let path = d3.select(selection.node().parentNode)
                .selectAll("path")
                .data([
                  "M " + (self.xScale(dataPoint.x) + d3.event.x - dataPoint.x + this.getComputedTextLength() / 2) + ", " +
                  (self.yScale(dataPoint.y) - dataPoint.y + d3.event.y) +
                  " A 30 30 0 0 0 " + self.xScale(dataPoint.x) + "," +
                  (self.yScale(dataPoint.y))
                ], p => p);

              path
                .attr("d", p => p)
                .style("stroke", function(d){ return fillColor(selection.data()[0].tag); })
                .style("fill", "none");

              path.exit().remove();

              path.enter()
                .insert("path","text")
                .attr("d", p => p)
                .style("stroke", function(d){ return fillColor(selection.data()[0].tag); })
                .style("fill", "none");

            })
          );
        })
        .on("dblclick", function(d) {
          const selection = d3.select(this);
          selection.selectAll("*:not(circle)").remove();
          selection.selectAll('.bubble')
            .filter(function(dBubble){
              return dBubble.bubbleName == d.bubbleName;
            })
            .attr('stroke-width', 2.5)
            .attr('stroke', function(d){ return fillColor(d.tag); });
        })
        .style('fill', function(d){ return fillColor(d.tag); });

        svg.append('text')
          .attr('x', this.WIDTH)
          .attr('y', this.HEIGHT + 35)
          .attr('text-anchor', 'middle')
          .attr('class', (this.light) ? 'label-light' : 'label-dark')
          .style("font-family", "NeueMontreal")
          .text(formatNumber(data.maxX.toFixed(1)));

        svg.append('text')
          .attr('x', 0)
          .attr('y', this.HEIGHT + 35)
          .attr('text-anchor', 'middle')
          .attr('class', (this.light) ? 'label' : 'label-dark')
          .style("font-family", "NeueMontreal")
          .text(formatNumber(data.minX.toFixed(1)) + " " + this.axisX.text);

        svg.append('text')
          .attr('x', - this.margin.left + 20)
          .attr('y', this.HEIGHT - 5)
          .attr('text-anchor', 'start')
          .attr('class', (this.light) ? 'label-light' : 'label-dark')
          .style("font-family", "NeueMontreal")
          .text(formatNumber(data.minY.toFixed(1)));


        svg.append('text')
          .attr('x', - this.margin.left + 20)
          .attr('y', - 5)
          .attr('text-anchor', 'start')
          .attr('class', (this.light) ? 'label' : 'label-dark')
          .style("font-family", "NeueMontreal")
          .text(formatNumber(data.maxY.toFixed(1)) + " " + this.axisY.text);

        let bubbleSizeY = this.$vuetify.breakpoint.xlOnly ? this.HEIGHT + (this.margin.bottom / 1.2) : this.HEIGHT + (this.margin.bottom / 1.2)
        svg.append('text')
          .attr('x', - this.margin.left + 50)
          .attr('y', bubbleSizeY)
          .attr('text-anchor', 'start')
          .attr('class', 'legendText')
          .style("font-size", "14px")
          .style("font-family", "NeueMontreal")
          .text('Bubble size is ' + this.getBubbbleSizeLegend(this.bubbleSize));

        let sizeIcon  = require(`@/assets/img/bubble-size.png`);
        svg.append("svg:image")
          .attr('x', - this.margin.left + 20)
          .attr('y', bubbleSizeY - 15)
          .attr('width', 20)
          .attr('height', 20)
          .attr("xlink:href", sizeIcon);

        let font = await loadFonts();
        copyAndAppendElement(svg.node(),font.node(),0,0);
      },
      getBubbbleSizeLegend(bubbleSize) {
        let text;
        switch (bubbleSize) {
          case "byMinutes":
            text = this.$t("scatterPlot.byMinutes");
            break;
          case "byAge":
            text = this.$t("scatterPlot.byAge");
            break;
          case "byMarketValue":
            text = this.$t("scatterPlot.byMarketValue");
            break;
          default:
        }
        //Set 'b' of By lowercase to show "by" instead "By"
        return text.charAt(0).toLowerCase() + text.slice(1);
      },
      getRightClassByColor(color) {
        switch (color) {
          case "#0051ff": return "legendTextBlue";
          case "#1abe51": return "legendTextDarkGreen";
          case "#00ff56": return "legendTextGreen";
          case "#aaa": return "legendTextGrey";
          case "#476F20": return "legendTextQ1";
          case "#73B240": return "legendTextQ2";
          case "#FCE230": return "legendTextQ3";
          case "#FBA180": return "legendTextQ4";
          case "#E7717D": return "legendTextPink";
          case "#C2B9B0": return "legendBrown";
          case "#C2CAD0": return "legendGrey2";
          case "#7E685A": return "legendTextDarkBrown";
          case "#F64030": return "legendTextRed";
          case "#008f95": return "legendTextBlueish";
          case "#4484CE": return "legendTextGreenish";
          case "#e9b000": return "legendTextYellowish";
          case "#eb6e80": return "legendTextRedish";
          default:
        }
      },
      getScatterColors(array, xReversed, yReversed){
        //Any are reversed, return normal colors
        if(!xReversed && !yReversed)
          return array;
        //Just X reversed
        if(xReversed && !yReversed)
          return [array[1],array[0],array[3],array[2]];
        //Just Y reversed
        if(!xReversed && yReversed)
          return [array[2],array[3],array[0],array[1]];
        //Both are reversed, return X and Y colors reversed
        if(xReversed && yReversed)
          return [array[3],array[2],array[1],array[0]];

        return array;
      },
    },
    destroyed(){
      d3.selectAll('.toolTip').remove();
    }
  }
</script>

<style>
.label {
			font-size: 12px;
      fill: #4A4A4A;
		}

    .label-dark{
      font-size: 12px;
      fill: #F5F5F5;
    }

    .label-light {
      font-size: 12px;
      fill: #636363;
    }

		.legend text,
		.axis text {
			font-size: 12px;
		}

		.axis path,
		.axis line{
			fill: none;
			stroke-width: 2px;
      stroke: #4A4A4A;
		}

    .axisDark path,
		.axisDark line{
      stroke-width: 2px;
      stroke: #bbb;
    }

    .axis2 text {
      font-size: 12px;
      fill: #999;
    }

    .axis2 path,
    .axis2 line{
      stroke: #bbb;
      stroke-width: 1px;
    }

    .grid {
      stroke: lightgrey;
      opacity: 0.5;
    }

		.circle {
			fill-opacity: 0.5;
		}

    .toolTip {
      font-size: 15px;
      font-family: NeueMontreal;
      pointer-events: none;
    	position: absolute;
      display: none;
      min-width: 50px;
      height: auto;
      background: none repeat scroll 0 0 #ffffff;
      padding: 9px 14px 9px 14px;
      border-radius: 4px;
      text-align: left;
      line-height: 1.5;
      color: #081F2C;
      box-shadow: 0px 3px 9px rgba(0, 0, 0, .15);
    }
    .text.label {
        font-size: 0.85rem;
    }
    .shadow{
      text-shadow:3px 3px 3px #fff, -3px -3px 3px #fff, -3px 3px 3px #fff, 3px -3px 3px #fff, -3px 0px 3px #fff, 3px 0px 3px #fff;
    }
    .shadow-dark{
      text-shadow:3px 3px 3px #000, -3px -3px 3px #000, -3px 3px 3px #000, 3px -3px 3px #000, -3px 0px 3px #000, 3px 0px 3px #000;
    }
    .toolTip span {
      font-size: 13px;
      font-family: NeueMontreal;
    	font-weight: 500;
      color: #5B6770;
    }
    .toolTipNumber {
      float: right;
      font-weight: 500;
      margin-top:2px;
      color: #5B6770;
    }

		.legend rect{
			fill-opacity: 1;
		}

    .legendText {
      font-weight: 600;
      font-size: 12px;
      fill: #858585;
    }

    .cornerLegendText { font-weight: 600; font-size: 20px; fill: #4A4A4A;}
    .cornerLegendTextDark { font-weight: 600; font-size: 20px; fill: #F5F5F5;}

    .legendTextBlue { font-weight: 600; font-size: 12px; fill: #0051ff;}
    .legendTextDarkGreen { font-weight: 600; font-size: 12px; fill: #1abe51;}
    .legendTextGreen { font-weight: 600; font-size: 12px; fill: #00ff56;}
    .legendTextGrey { font-weight: 600; font-size: 12px; fill: #aaa;}
    .legendTextQ1 { font-weight: 600; font-size: 12px; fill: #476F20;}
    .legendTextQ2 { font-weight: 600; font-size: 12px; fill: #73B240;}
    .legendTextQ3 { font-weight: 600; font-size: 12px; fill: #FCE230;}
    .legendTextQ4 { font-weight: 600; font-size: 12px; fill: #FBA180;}
    .legendTextQ5 { font-weight: 600; font-size: 12px; fill: #F64030;}
    .legendTextPink { font-weight: 600; font-size: 12px; fill: #E7717D;}
    .legendBrown { font-weight: 600; font-size: 12px; fill: #C2B9B0;}
    .legendGrey2 { font-weight: 600; font-size: 12px; fill: #C2CAD0;}
    .legendTextDarkBrown { font-weight: 600; font-size: 12px; fill: #7E685A;}
    .legendTextRed { font-weight: 600; font-size: 12px; fill: #F64030;}
    .legendTextBlueish { font-weight: 600; font-size: 12px; fill: #008f95;}
    .legendTextGreenish { font-weight: 600; font-size: 12px; fill: #4484CE;}
    .legendTextYellowish { font-weight: 600; font-size: 12px; fill: #e9b000;}
    .legendTextRedish { font-weight: 600; font-size: 12px; fill: #eb6e80;}

		.legend:hover rect{
			fill-opacity:1;
		}
</style>



// WEBPACK FOOTER //
// src/views/base/ScatterPlotChart.vue